import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, ListGroup, ListGroupItem, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { fetchGoals, fetchNextTasks } from "../../../../services/httpClient";
import { Goal } from "../../../../types/goal";
import { Task } from "../../../../types/task";
import ToastMsg from "../../../ui/ToastMsg";
import GoalCard from "./GoalCard";
import TaskItem from "./TaskItem";
import CreateGoalCard from "../../../ui/CreateGoalCard";
import PageTitle from "../../../ui/PageTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleDot } from "@fortawesome/free-solid-svg-icons";
import UseGoalTemplate from "./UseGoalTemplate";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";

interface GoalsState {
  viewGoals: boolean;
  setViewGoals: any;
  viewNextTasks: boolean;
  setViewNextTasks: any;
  viewCompleted: boolean;
  setViewCompleted: any;
  viewArchive: boolean;
  setViewArchive: any;
  createGoal: boolean;
  setCreateGoal: any;
  myCase: boolean;
  getData: boolean;
  family: any;
}

const Goals = ({
  viewGoals,
  setViewGoals,
  viewNextTasks,
  setViewNextTasks,
  viewCompleted,
  setViewCompleted,
  viewArchive,
  setViewArchive,
  createGoal,
  setCreateGoal,
  myCase,
  getData,
  family,
}: GoalsState) => {
  const { id } = useParams();
  const [currentGoals, setCurrentGoals] = useState<Goal[]>();
  const [completedGoals, setCompletedGoals] = useState<Goal[]>();
  const [archivedGoals, setArchivedGoals] = useState<Goal[]>();
  const [filteredCurrentGoals, setFilteredCurrentGoals] = useState<Goal[]>([]);
  const [filteredCompletedGoals, setFilteredCompletedGoals] = useState<Goal[]>([]);
  const [filteredArchivedGoals, setFilteredArchivedGoals] = useState<Goal[]>([]);
  const [next, setNext] = useState<Task[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [goalId, setGoalId] = useState<string>("");
  const [goalSaved, setGoalSaved] = useState<boolean>(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [goalsNewActive, setGoalsNewActive] = useState<boolean>(true);
  const [goalsTemplateActive, setGoalsTemplateActive] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>();
  const [subject, setSubject] = useState<any>({
    value: "0",
    label: "View All",
    original: { key: "0", name: "View All" },
  });

  const updateSubject = (e: any) => {
    if (!e) {
      setSubject({
        value: "0",
        label: "View All",
        original: { key: "0", name: "View All" },
      });
      return;
    }

    const formatted = {
      ...e,
      value: e.id ?? e.key ?? e.value,
      label: e.name ?? e.label ?? "",
    };
    setSubject(formatted);
  };

  const updateViewGoals = (e: any) => {
    setErrMsg("");
    setViewGoals(e);
    setViewNextTasks(false);
    setViewCompleted(false);
    setViewArchive(false);
    setCreateGoal(false);
    getGoals();
    setGoalSaved(false);
    setSubject("0");

    // trackNavigationEvent("switch_view", "goals");
  };

  const updateViewNextTasks = (e: any) => {
    setErrMsg("");
    setViewGoals(false);
    setViewNextTasks(e);
    setViewCompleted(false);
    setViewArchive(false);
    setCreateGoal(false);
    getGoals();
    setGoalSaved(false);

    // trackNavigationEvent("switch_view", "next_tasks");
  };

  const updateViewCompleted = (e: any) => {
    setErrMsg("");
    setViewGoals(false);
    setViewNextTasks(false);
    setViewCompleted(e);
    setViewArchive(false);
    setCreateGoal(false);
    setSubject("0");

    // trackNavigationEvent("switch_view", "completed_goals");
  };

  const updateViewArchive = (e: any) => {
    setErrMsg("");
    setViewGoals(false);
    setViewNextTasks(false);
    setViewCompleted(false);
    setViewArchive(e);
    setCreateGoal(false);
    setSubject("0");

    // trackNavigationEvent("switch_view", "archived_goals");
  };

  const updateCreateGoal = (e: any) => {
    setErrMsg("");
    setViewGoals(false);
    setViewNextTasks(false);
    setViewCompleted(false);
    setViewArchive(false);
    setCreateGoal(e);
  };

  const getGoals = () => {
    fetchGoals(id)
      .then((res: any) => {
        const allGoals = res[0].data;

        const current = allGoals.filter((n: any) => n.is_completed === 0 && n.is_archived === 0);
        setCurrentGoals(current);
        setFilteredCurrentGoals(current);
        fetchNextTasks(id)
          .then((res: any) => {
            const nextTasks = res[0].data;
            nextTasks.forEach((n: Task) => {
              if (n.next_at) {
                var nextDate = new Date(n.next_at);
                nextDate = new Date(nextDate.setDate(nextDate.getDate())); //subtract here to test diff
                nextDate.setHours(nextDate.getHours() - 5);
                const currentDate = new Date();
                const timeDiff = currentDate.getTime() - nextDate.getTime();
                const daysDiff = Math.floor(timeDiff / (24 * 60 * 60 * 1000));
                n.age = daysDiff;
              }
            });

            setNext(nextTasks);
          })
          .catch((err: any) => {
            console.log(err);
            setIsLoading(false);
          });

        const completed = allGoals.filter((n: any) => n.is_completed === 1 && n.is_archived !== 1);
        setCompletedGoals(completed);

        const archive = allGoals.filter((n: any) => n.is_archived === 1);
        setArchivedGoals(archive);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getGoals();
    }
  }, [getData]);

  const familyMembers = useMemo(() => {
    const normalized = family.map((f: any) => ({
      id: f.id,
      type: f.role === "family" ? "guardian" : "member",
      key: `${f.firstname} ${f.lastname}`,
      name: `${f.firstname} ${f.lastname}`,
      isDeleted: f.deleted_at ? true : false,
      isBanned: f.is_banned === 1 ? true : false,
    }));

    const unassigned = {
      id: "Unassigned",
      type: "Unassigned",
      key: "Unassigned",
      name: "Unassigned",
      isDeleted: false,
      isBanned: false,
    };

    return [unassigned, ...normalized].sort((a, b) => a.isDeleted - b.isDeleted);
  }, [family]);

  const activeFamily = useMemo(() => {
    const filtered = familyMembers.filter((f: any) => !f.isDeleted);
    return [...filtered].sort((a, b) => a.isBanned - b.isBanned);
  }, [familyMembers]);

  const updateGoalSelection = (type: string) => {
    switch (type) {
      case "new":
        setGoalsNewActive(true);
        setGoalsTemplateActive(false);
        break;
      case "template":
        setGoalsNewActive(false);
        setGoalsTemplateActive(true);
        break;
      default:
        setGoalsNewActive(true);
        setGoalsTemplateActive(false);
        break;
    }
  };

  const setErr = (err: any) => {
    setErrMsg(err);
  };

  useEffect(() => {
    const filterBySubject = (goals: any) => {
      if (!goals) {
        return [];
      }

      const subjectKey = subject?.value || "0";
      const subjectName = subject?.label || "View All";
      const isSubjectViewAll = subjectKey === "0" || subjectName === "View All";

      if (subjectKey === "unassigned") {
        return goals.filter((goal: any) => !goal.subject || !goal.subject.name);
      }

      if (isSubjectViewAll) {
        return goals;
      }

      return goals.filter((goal: any) => goal.subject?.name === subjectName);
    };

    setFilteredCurrentGoals(filterBySubject(currentGoals));
    setFilteredCompletedGoals(filterBySubject(completedGoals));
    setFilteredArchivedGoals(filterBySubject(archivedGoals));
  }, [subject, currentGoals, completedGoals, archivedGoals]);

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {viewGoals === true && (
            <>
              <Row className="mb-0">
                <Col xs={1} lg={3}>
                  <h4 className="app-header-text">Current Goals</h4>
                </Col>
                <Col xs={11} lg={9} className="text-end">
                  <Button className="app-secondary-color me-3 button" onClick={() => updateViewNextTasks(true)}>
                    Next Tasks
                  </Button>
                  <Button className="app-secondary-color me-3 button" onClick={() => updateViewCompleted(true)}>
                    Completed
                  </Button>
                  <Button className="app-secondary-color me-3 button" onClick={() => updateViewArchive(true)}>
                    Archived
                  </Button>
                  {myCase && (
                    <Button className="app-primary-color button" onClick={() => updateCreateGoal(true)}>
                      Create Goal
                    </Button>
                  )}
                </Col>
              </Row>
              {currentGoals?.length !== 0 && (
                <Row>
                  <Col className="col-6 p-0">
                    <SelectObjectTypeahead
                      initialOption="View All"
                      label="Household Member"
                      value={subject}
                      options={(() => {
                        const viewAll = { value: "0", label: "View All", original: { key: "0", name: "View All" } };
                        const unassigned = {
                          value: "unassigned",
                          label: "Unassigned",
                          original: { key: "unassigned", name: "Unassigned" },
                        };
                        const active = familyMembers
                          .filter((f: any) => !f.isDeleted && f.id !== "Unassigned")
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const deleted = familyMembers
                          .filter((f: any) => f.isDeleted)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const groups: any[] = [];
                        if (active.length) groups.push({ label: "Active", options: active });
                        if (deleted.length) groups.push({ label: "Deleted", options: deleted });
                        return [viewAll, unassigned, ...groups];
                      })()}
                      groupHeading={true}
                      onUpdate={updateSubject}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                {filteredCurrentGoals?.length === 0 ? (
                  <p className="mt-2">No Current Goals</p>
                ) : (
                  filteredCurrentGoals?.map((g: Goal) => (
                    <GoalCard
                      key={g.id}
                      goal={g}
                      family={activeFamily}
                      fetchGoals={getGoals}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      myCase={myCase}
                      setErrMsg={setErrMsg}
                    />
                  ))
                )}
              </Row>
            </>
          )}
          {viewNextTasks === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Next Tasks</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewGoals(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              <Row>
                {next?.length === 0 && <p className="mt-2">No Next Tasks</p>}
                <ListGroup className="pe-0 pb-5">
                  {next?.map((n: any) => (
                    <ListGroupItem key={n.id}>
                      <TaskItem task={n} goalArchived={0} goalCompleted={0} inNextTasks={true} myCase={myCase} />
                      <text className="text-muted">{n.goal_name}</text>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </Row>
            </>
          )}
          {viewCompleted === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Completed Goals</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewGoals(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              {completedGoals?.length !== 0 && (
                <Row>
                  <Col className="col-6 p-0">
                    <SelectObjectTypeahead
                      initialOption="View All"
                      label="Household Member"
                      value={subject}
                      options={(() => {
                        const viewAll = { value: "0", label: "View All", original: { key: "0", name: "View All" } };
                        const unassigned = {
                          value: "unassigned",
                          label: "Unassigned",
                          original: { key: "unassigned", name: "Unassigned" },
                        };
                        const active = familyMembers
                          .filter((f: any) => !f.isDeleted && f.id !== "Unassigned")
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const deleted = familyMembers
                          .filter((f: any) => f.isDeleted)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const groups: any[] = [];
                        if (active.length) groups.push({ label: "Active", options: active });
                        if (deleted.length) groups.push({ label: "Deleted", options: deleted });
                        return [viewAll, unassigned, ...groups];
                      })()}
                      groupHeading={true}
                      onUpdate={updateSubject}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                {filteredCompletedGoals?.length === 0 ? (
                  <p className="mt-2">No Completed Goals</p>
                ) : (
                  filteredCompletedGoals?.map((a: any) => (
                    <GoalCard
                      key={a.id}
                      goal={a}
                      family={familyMembers}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErr}
                    />
                  ))
                )}
              </Row>
            </>
          )}
          {viewArchive === true && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Archived Goals</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewGoals(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              {archivedGoals?.length !== 0 && (
                <Row>
                  <Col className="col-6 p-0">
                    <SelectObjectTypeahead
                      initialOption="View All"
                      label="Household Member"
                      value={subject}
                      options={(() => {
                        const viewAll = { value: "0", label: "View All", original: { key: "0", name: "View All" } };
                        const unassigned = {
                          value: "unassigned",
                          label: "Unassigned",
                          original: { key: "unassigned", name: "Unassigned" },
                        };
                        const active = familyMembers
                          .filter((f: any) => !f.isDeleted && f.id !== "Unassigned")
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const deleted = familyMembers
                          .filter((f: any) => f.isDeleted)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const groups: any[] = [];
                        if (active.length) groups.push({ label: "Active", options: active });
                        if (deleted.length) groups.push({ label: "Deleted", options: deleted });
                        return [viewAll, unassigned, ...groups];
                      })()}
                      groupHeading={true}
                      onUpdate={updateSubject}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                {filteredArchivedGoals?.length === 0 ? (
                  <p className="mt-2">No Archived Goals</p>
                ) : (
                  filteredArchivedGoals?.map((a: any) => (
                    <GoalCard
                      key={a.id}
                      goal={a}
                      family={familyMembers}
                      fetchGoals={getGoals}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      myCase={myCase}
                      setErrMsg={setErr}
                    />
                  ))
                )}
              </Row>
            </>
          )}
          {createGoal === true && (
            <>
              <PageTitle
                title="Create Goal"
                type="section"
                buttonColor="primary"
                buttonTitle="Cancel"
                onButtonClick={() => updateViewGoals(true)}
              />
              <Row className="mb-3">
                <Col></Col>
                <Col xs={4} lg={3} className="d-grid">
                  <Button
                    className={goalsNewActive ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => updateGoalSelection("new")}
                  >
                    <FontAwesomeIcon icon={goalsNewActive ? faCircleCheck : faCircleDot} className={"me-2"} />
                    {"Enter New"}
                  </Button>
                </Col>
                <Col xs={4} lg={3} className="d-grid">
                  <Button
                    className={goalsTemplateActive ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={(e) => updateGoalSelection("template")}
                  >
                    <FontAwesomeIcon icon={goalsTemplateActive ? faCircleCheck : faCircleDot} className={"me-2"} />
                    {"Use Template"}
                  </Button>
                </Col>
                <Col></Col>
              </Row>
              {goalsNewActive && (
                <CreateGoalCard
                  goalSaved={goalSaved}
                  setGoalSaved={setGoalSaved}
                  family={activeFamily}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              )}
              {goalsTemplateActive && (
                <UseGoalTemplate
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                  family={activeFamily}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Goals;
